import React, {KeyboardEvent, MouseEvent, useCallback, useEffect, useRef} from 'react';
import {
  VALUE_ALT,
  VALUE_BACK_SPACE,
  VALUE_CAPS_LOCK,
  VALUE_CONTROL,
  VALUE_DELETE,
  VALUE_DOWN,
  VALUE_ENTER,
  VALUE_LEFT,
  VALUE_META,
  VALUE_RIGHT,
  VALUE_SHIFT,
  VALUE_SPACE,
  VALUE_TAB,
  VALUE_UP,
} from './keycode';

import {makeStyles} from '@material-ui/core/styles';

import {
  addSymbol,
  getCurrentCursorPosition,
  getCurrentIsField,
  getNextIsField,
  getPrevIsField,
  insertNewLineSymbol,
  jumpToEnd,
  jumpToStart,
  removeNext,
  removePrev,
  setCursorPosition,
} from './cursor-position';

interface EditableRawProps {
  onChange: (htmlContent: string, cursorPosition: [number, number]) => void;
  setTarget: (e: any) => void;
  value?: string;
  moveToPos: number;
  shouldBeMoved: boolean;
}

export const EditableRaw: React.FC<EditableRawProps> = ({
  onChange,
  setTarget,
  value = '',
  moveToPos,
  shouldBeMoved,
}) => {
  const handleClick = useCallback(
    (evt: MouseEvent) => {
      if ((evt.target as any)?.tagName === 'B') {
        evt.preventDefault();
        evt.stopPropagation();
        setTarget(evt.target);
      } else {
        setTarget(null);
      }
    },
    [setTarget]
  );

  const contentEditable = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (moveToPos > -1 && contentEditable.current && shouldBeMoved) {
      setCursorPosition(contentEditable.current, moveToPos);
    }
  }, [contentEditable, moveToPos, value, shouldBeMoved]);

  const handleChange = useCallback(() => {
    if (contentEditable.current) {
      const cursorPosition = getCurrentCursorPosition(contentEditable.current);
      onChange(contentEditable.current.innerHTML, cursorPosition);
    }
  }, [contentEditable, onChange]);

  const handleKeyDown = useCallback<(evt: KeyboardEvent) => Promise<void>>(
    async (evt: KeyboardEvent) => {
      const isPrevField = getPrevIsField();
      const isNextField = getNextIsField();
      const isCurrentField = getCurrentIsField();

      if (evt.key === VALUE_ENTER) {
        evt.preventDefault();
        insertNewLineSymbol();
        return;
      }

      if (evt.key === VALUE_LEFT) {
        if (isPrevField) {
          evt.preventDefault();
          jumpToStart();
          return;
        }
      }

      if (evt.key === VALUE_RIGHT) {
        if (isNextField) {
          evt.preventDefault();
          jumpToEnd();
          return;
        }
      }

      if (evt.key === VALUE_BACK_SPACE) {
        if (isPrevField) {
          evt.preventDefault();
          removePrev();
          return;
        }
      }

      if (evt.key === VALUE_DELETE) {
        if (isNextField) {
          evt.preventDefault();
          removeNext();
          return;
        }
      }

      if (evt.key === VALUE_SPACE) {
        if (isPrevField) {
          evt.preventDefault();
          addSymbol();
          return;
        }
      }

      if (
        ![
          // TODO: ADD ADDITIONAL KEY VALUES FROM WINDOWS SYSTEM (DON'T HAVE IT)
          VALUE_LEFT,
          VALUE_RIGHT,
          VALUE_DOWN,
          VALUE_UP,
          VALUE_META,
          VALUE_SHIFT,
          VALUE_CONTROL,
          VALUE_ALT,
          VALUE_TAB,
          VALUE_CAPS_LOCK,
        ].includes(evt.key) &&
        !evt.ctrlKey &&
        !evt.metaKey
      ) {
        if (isCurrentField) {
          evt.preventDefault();
          addSymbol(evt.key);
          return;
        }

        if (isPrevField) {
          evt.preventDefault();
          addSymbol(evt.key);
          return;
        }
      }
    },
    []
  );

  const {root} = useStyles();
  return (
    <div
      role="textbox"
      aria-multiline="true"
      ref={contentEditable}
      className={root}
      onClick={handleClick}
      onBlur={handleChange}
      onKeyDown={handleKeyDown}
      contentEditable
      dangerouslySetInnerHTML={{
        __html: value,
      }}
    />
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    color: theme.palette.text.primary,
    fontSize: '14px',
    lineHeight: '24px',
    outline: 'none',
    whiteSpace: 'pre-line',
    '& > b': {
      cursor: 'pointer',
      textDecoration: 'underline',
      whiteSpace: 'pre-line',
    },
  },
}));
